"use client";
import { notFound, useParams } from "next/navigation";
import {
  FolderContext,
  FolderTreeItem,
  useFolderActionPermissions,
  useFolderAncestors,
  useQueryFolderDiscovery,
  useFolderTreeFolder,
  useFolderAssets,
  useSplitTreatments,
  useBulkCheckMovePermissions,
  useBulkCheckDeletePermissions,
  HydratedZapAsset,
  sortAssets,
  getNextSortState,
  toTrashIndexSort,
  filterAssetsBySearch,
  filterSubfoldersBySearch,
  AssetSortState,
  HydratedAsset,
  convertToAssetId,
} from "@zapier/client";
import {
  EmptyTableDisplay,
  AccountMismatchModal,
  useEmitFolderActionEvent,
  useEmitFolderAssetSortEvent,
  useEmitFolderDocumentationInteractionEvent,
  FolderActionInteractionOption,
  useRouterParams,
  Pagination,
  PageSizeProvider,
  usePageSize,
  BulkActionsBarWrapper,
  BulkMoveModal,
  AssetDeleteModal,
  BulkModal,
  CopyToAnotherAccountModal,
  ZapModals,
  useFolderAndZapsModalStates,
  FolderModal,
  useModalUrlParameters,
  logWithNotification,
} from "@zapier/shared";
import {
  AssetTypes,
  FOLDER_ASSETS_SORT_LIMIT,
  FolderTypeQueryState,
  getCreateZapUrl,
  isPersonalFolder,
  isTrashFolder,
  isVirtualFolderNotDefault,
  parseFolderId,
  useNotification,
} from "@zapier/utils";
import { FolderHeader } from "../FolderHeader";
import { AssetTable } from "./AssetTable";
import { hasRecognizedAssetType } from "./hasRecognizedAssetType";
import { useBulkSelectState } from "./useBulkSelectState";
import { useMemo, useState, useCallback, useEffect } from "react";
import { FolderModals } from "../FolderModals";

import {
  Alert,
  BetaDataTableProvider as TableProvider,
  Field,
  SortDescriptor,
  Tabs,
  Text,
  TextInput,
} from "@zapier/design-system";

import { Icon } from "@zapier/zinnia-icons";
import { useTranslations } from "@zapier/i18n";
import { usePinnedCanvas } from "../../hooks/usePinnedCanvas";
import { useGenerateCanvasFromFolderAssets } from "../../hooks/useGenerateCanvasFromFolderAssets";
import styles from "./AssetTable.module.css";
import { FolderDocumentation } from "./FolderDocumentation";

function FolderWithPageSize() {
  const t = useTranslations("folder-asset-table");
  const tBulkBar = useTranslations("bulk-actions-bar");

  const parameters = useParams();
  const { memberId, page, setParams } = useRouterParams();
  const { size: pageSize } = usePageSize();
  const folderId = parseFolderId(String(parameters?.folder));
  const isPersonalFolderRoute = isPersonalFolder(String(parameters?.folder));

  const [activeTab, setActiveTab] = useState<"assets" | "documentation">(
    "assets",
  );

  const {
    isBulkMoveAssetsEnabled,
    isLastRunEnabled,
    isDuplicateAssetsEnabled,
    isZapAndCanvasUnifiedTrashEnabled,
    isTrashIndexEnabled,
  } = useSplitTreatments();
  const isTrashFolderRoute = isTrashFolder(folderId);
  // Mirrors the BFF gate: on this path the server sorts, searches and paginates.
  const isTrashIndexPath =
    isTrashFolderRoute &&
    isTrashIndexEnabled &&
    isZapAndCanvasUnifiedTrashEnabled;
  const shouldShowDocumentationTab =
    !isPersonalFolderRoute && !isTrashFolderRoute;

  const syncTabWithHash = useCallback(() => {
    if (globalThis.location === undefined) return;

    setActiveTab(
      globalThis.location.hash === "#documentation"
        ? "documentation"
        : "assets",
    );
  }, []);

  // Prevent the browser's native "scroll to #hash" behavior for these tab links.
  // We'll still keep the hash in the URL (via history.pushState) but without scrolling.
  const handleTabsClickCapture = useCallback((event: React.MouseEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const anchor = target.closest("a");
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    if (href === "#assets" || href === "#documentation") {
      event.preventDefault();
    }
  }, []);

  // Sort state for frontend sorting
  const [sortState, setSortState] = useState<AssetSortState>({
    field: undefined,
    direction: "asc",
  });

  // Search state for frontend filtering
  const [searchQuery, setSearchQuery] = useState("");

  const { createNotification } = useNotification();
  const { emit } = useEmitFolderActionEvent();
  const { emit: emitSortEvent } = useEmitFolderAssetSortEvent();
  const { emit: emitFolderDocumentationEvent } =
    useEmitFolderDocumentationInteractionEvent();

  const [autoLayoutCanvasId, setAutoLayoutCanvasId] = useState<
    string | undefined
  >();
  // Used as a one-shot autolayout trigger for the pinned canvas embed:
  // after generating/regenerating, we temporarily set this to the canvas id so the iframe
  // loads with `?autolayout=1` once, then it is cleared to avoid re-layout on refresh.

  const isSearchActive = searchQuery.trim().length > 0;

  // Only the in-memory sort and filter path needs every asset up front.
  const isFetchingAll =
    !isTrashIndexPath && (Boolean(sortState.field) || isSearchActive);
  const effectivePageSize = isFetchingAll ? FOLDER_ASSETS_SORT_LIMIT : pageSize;
  const { ancestors } = useFolderAncestors({
    folderId,
  });

  const { canCreateSubfolderLimit, subfolderLimitMessage, isReadOnly } =
    useFolderActionPermissions({
      folderId,
      folderDepth: (ancestors?.length || 0) + 1,
    });

  const {
    folder,
    isLoading: isFoldersLoading,
    totalSubfolderPages,
    paginatedSubfolders,
    isError: folderError,
    errorStatus: folderErrorStatus,
  } = useFolderTreeFolder({ folderId, page, includeChildren: true });

  const {
    pinnedCanvasId,
    isPinnedCanvasIdLoading,
    isPinning,
    isCreating,
    handlePinCanvas,
    handleUnpinCanvas,
    handleCreateCanvas,
  } = usePinnedCanvas({
    folderId,
    isFolderByIdEnabled:
      shouldShowDocumentationTab && activeTab === "documentation",
    folderName: folder?.name,
  });
  // ------------------------------
  // Hydrate all folder assets
  // ------------------------------

  const {
    assets: rawAssets,
    isLoading: isAssetsLoading,
    meta: assetsMeta,
    totalPages: totalAssetsPages,
    totalCount,
    errorStatus: assetsErrorStatus,
    isError: assetsError,
  } = useFolderAssets({
    folderId,
    page: isFetchingAll ? 1 : page || 1,
    pageSize: effectivePageSize,
    subfoldersOffset: isFetchingAll
      ? 0
      : isFoldersLoading
        ? 25
        : folder?.children?.length,
    ownerId: memberId ? Number(memberId) : undefined,
    search: isTrashIndexPath && isSearchActive ? searchQuery.trim() : undefined,
    sort: isTrashIndexPath ? toTrashIndexSort(sortState) : undefined,
  });

  // Sorting is enabled with fallback to asset count limit
  const isOverAssetLimit = (totalCount || 0) > FOLDER_ASSETS_SORT_LIMIT;
  const isSortDisabledDueToLimit = isOverAssetLimit && !isTrashIndexPath;
  const isSortEnabled = isTrashIndexPath || !isOverAssetLimit;

  const isSearchEnabled = isTrashIndexPath || !isOverAssetLimit;

  // Show tooltip explaining why sorting is disabled when over asset limit
  const sortDisabledTooltip = isSortDisabledDueToLimit
    ? t("sort.disabled-tooltip" as const)
    : undefined;

  // The flag gate is read independently here and in the BFF, so `isTrashIndexPath`
  // cannot prove the server searched. Only the echoed term can.
  const isSearchAppliedByServer =
    isSearchActive && assetsMeta?.appliedSearchTerm === searchQuery.trim();

  const isFilteringLocally =
    isSearchActive && isSearchEnabled && !isSearchAppliedByServer;

  // Sort and filter assets on the frontend
  const assets = useMemo(() => {
    let result = rawAssets as HydratedAsset[];

    if (sortState.field && isSortEnabled && !isTrashIndexPath) {
      result = sortAssets(
        result,
        sortState.field,
        sortState.direction,
        isTrashFolderRoute,
      );
    }

    if (isFilteringLocally) {
      result = filterAssetsBySearch(result, searchQuery);
    }

    return result;
  }, [
    rawAssets,
    isTrashIndexPath,
    isTrashFolderRoute,
    sortState.field,
    sortState.direction,
    isSortEnabled,
    isFilteringLocally,
    searchQuery,
  ]);

  // Unless every row was fetched up front, the local filter only sees the
  // current page, so the server's count and page links describe a different
  // result set than the rows on screen.
  const hasServerPagination = !isFetchingAll && !isFilteringLocally;

  const {
    isGeneratingFromFolderAssets,
    hasEligibleFolderAssetIds,
    generateNewCanvasFromFolderAssets,
    regeneratePinnedCanvasFromFolderAssets,
  } = useGenerateCanvasFromFolderAssets({
    folderId,
    folderName: folder?.name,
    assets: assets || [],
    pinnedCanvasId,
  });

  // A server-side sort or search rebuilds the result set; a stale offset would
  // land on an empty page.
  const resetPageOnServerQuery = useCallback(() => {
    if (isTrashIndexPath && (page ?? 1) > 1) setParams({ page: 1 });
  }, [isTrashIndexPath, page, setParams]);

  // Handle sort change from the table header
  const handleSortChange = useCallback(
    (descriptor: SortDescriptor) => {
      resetPageOnServerQuery();
      setSortState((previous) => {
        const nextState = getNextSortState(previous, descriptor);
        emitSortEvent({
          sortDirection: nextState.field ? nextState.direction : "none",
          assetCount: totalCount || 0,
          folderId,
        });
        return nextState;
      });
    },
    [emitSortEvent, totalCount, folderId, resetPageOnServerQuery],
  );

  const sortDescriptor: SortDescriptor | undefined = sortState.field
    ? {
        column: sortState.field,
        direction: sortState.direction === "asc" ? "ascending" : "descending",
      }
    : undefined;

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      resetPageOnServerQuery();
      setSearchQuery(event.target.value);
    },
    [resetPageOnServerQuery],
  );

  const handleClearSearch = useCallback(() => {
    resetPageOnServerQuery();
    setSearchQuery("");
  }, [resetPageOnServerQuery]);

  // When searching, filter ALL subfolders (not just the current page slice)
  // so results span every pagination page.
  const filteredSubfolders = useMemo(() => {
    let subfolders: FolderTreeItem[];
    const allChildren = (folder?.children || []) as FolderTreeItem[];
    if (isSearchActive) {
      subfolders = filterSubfoldersBySearch(allChildren, searchQuery);
    } else if (isFetchingAll) {
      subfolders = allChildren;
    } else {
      subfolders = paginatedSubfolders || [];
    }

    if (sortState.field === "name" && isSortEnabled) {
      const multiplier = sortState.direction === "desc" ? -1 : 1;
      subfolders = [...subfolders].sort(
        (a, b) => (a.name || "").localeCompare(b.name || "") * multiplier,
      );
    }

    return subfolders;
  }, [
    folder?.children,
    paginatedSubfolders,
    isSearchActive,
    isFetchingAll,
    searchQuery,
    sortState.field,
    sortState.direction,
    isSortEnabled,
  ]);

  const selectableEntities = useMemo(() => {
    const assetEntities = assets
      .filter(hasRecognizedAssetType)
      .map((asset) => ({
        id: convertToAssetId(asset.type, asset.id),
      }));
    const folderEntities = (filteredSubfolders || []).map((f) => ({
      id: `folder:${f.id}`,
    }));
    return [...folderEntities, ...assetEntities];
  }, [assets, filteredSubfolders]);

  // ------------------------------
  // Handle folder mismatch
  // ------------------------------

  const { accountInfo, isLoading: isFolderDiscoveryLoading } =
    useQueryFolderDiscovery({
      folderId,
      hasError: folderError || assetsError,
    });

  // ------------------------------
  // Bulk section
  // ------------------------------

  const { check, ...permissionResult } = useBulkCheckMovePermissions();
  const { check: checkDelete, ...deletePermissionResult } =
    useBulkCheckDeletePermissions();

  const handleMoveClicked = useCallback(
    (ids: string[]) => {
      check(ids);
    },
    [check],
  );

  const handleDeleteClicked = useCallback(
    (ids: string[]) => {
      checkDelete(ids);
    },
    [checkDelete],
  );

  const {
    bulkSelectItems,
    selectedZaps,
    clearSelection,
    setCurrentZapModal,
    currentZapModal,
    currentBulkModal,
    setCurrentBulkModal,
    selectedTypedAssetIds,
    bulkCopyState,
  } = useBulkSelectState({
    folder,
    folderId,
    assets,
    filteredSubfolders,
    onMoveClicked: handleMoveClicked,
    onDeleteClicked: handleDeleteClicked,
    isCopyToAnotherAccountEnabled: isDuplicateAssetsEnabled,
  });

  // Raw-id -> display name, for the "won't be deleted" list in the bulk
  // permanent-delete modal.
  const assetNamesById = useMemo(() => {
    const names: Record<string, string> = {};
    for (const asset of assets) {
      const name =
        asset.type === AssetTypes.Zap && "title" in asset
          ? (asset.title as string)
          : "name" in asset
            ? (asset.name as string)
            : undefined;
      if (name) {
        names[asset.id] = name;
      }
    }
    return names;
  }, [assets]);

  // Lets the bulk delete modal's title/button name the asset when exactly
  // one ends up deletable, instead of falling back to count-based copy.
  // Mirrors the modal's own itemCount fallback: before the permission check
  // resolves, go off the raw selection; after, go off the deletable subset
  // (which can narrow a multi-select down to one deletable asset).
  const singleDeleteAssetName = useMemo(() => {
    const { deletableIds, hasChecked, isLoading } = deletePermissionResult;
    const effectiveIds =
      hasChecked && !isLoading ? deletableIds : selectedTypedAssetIds;

    if (effectiveIds.length !== 1) {
      return undefined;
    }
    const [typedId] = effectiveIds;
    const separatorIndex = typedId.indexOf(":");
    const rawId =
      separatorIndex === -1 ? typedId : typedId.slice(separatorIndex + 1);
    return assetNamesById[rawId] ?? assetNamesById[typedId];
  }, [assetNamesById, deletePermissionResult, selectedTypedAssetIds]);

  const {
    currentFolder,
    setCurrentFolder,
    currentFolderModal,
    setCurrentFolderModal,
    currentZap,
    setCurrentZap,
  } = useFolderAndZapsModalStates();

  const zaps = useMemo(() => {
    return assets.filter(
      (asset) => asset.type === AssetTypes.Zap,
    ) as HydratedZapAsset[];
  }, [assets]);

  // Handle modal URL parameters with orientation delay
  const { closeModalAndClearParameters } = useModalUrlParameters({
    setCurrentFolderModal,
    setCurrentZapModal,
    setCurrentFolder,
    setCurrentZap,
    folder,
    zaps,
    isLoading: isAssetsLoading || isFoldersLoading || isFolderDiscoveryLoading,
  });

  // Current Zap is used for URL-triggered modals to render the correct modal
  // Selected Zaps are used for bulk actions to render the correct modal
  const currentZaps = useMemo(() => {
    if (currentZap) {
      return [currentZap];
    }

    if (selectedZaps.length > 0) {
      return selectedZaps;
    }
  }, [selectedZaps, currentZap]);

  useEffect(() => {
    if (globalThis.window === undefined) return;
    if (!shouldShowDocumentationTab) return;

    // Sync once when the doc tab is enabled (covers initial load + flag hydration)
    syncTabWithHash();

    globalThis.window.addEventListener("popstate", syncTabWithHash);

    return () => {
      globalThis.window.removeEventListener("popstate", syncTabWithHash);
    };
  }, [shouldShowDocumentationTab, syncTabWithHash]);

  if (
    (folderErrorStatus === 403 || assetsErrorStatus === 403) &&
    accountInfo?.name &&
    !isFolderDiscoveryLoading &&
    !isFoldersLoading &&
    !isAssetsLoading
  ) {
    return (
      <AccountMismatchModal
        account={accountInfo}
        noun="folder"
        continueText={t("navigation.continue-to-folders")}
      />
    );
  }

  if (
    (folderErrorStatus === 401 ||
      folderErrorStatus === 404 ||
      assetsErrorStatus === 401 ||
      assetsErrorStatus === 404) &&
    !isAssetsLoading &&
    !isFoldersLoading &&
    !isFolderDiscoveryLoading
  ) {
    const isFolderNotInTree = folderErrorStatus === 401 && !folderError;

    logWithNotification(
      `Folder notFound triggered - folderId: ${folderId}`,
      "warning",
      {
        isFolderNotInTree,
        folderErrorStatus,
        assetsErrorStatus,
        isAssetsLoading,
        isFoldersLoading,
        isFolderDiscoveryLoading,
        folderId,
      },
    );
    return notFound();
  }

  const hasRawAssets = (rawAssets?.length || 0) > 0;
  const hasSubfolders = (folder?.children?.length || 0) > 0;
  const hasFolderContent = hasRawAssets || hasSubfolders;

  // An active search that matches nothing is a no-results state, not an empty folder.
  const shouldShowEmptyFolderState =
    !isAssetsLoading &&
    !isFoldersLoading &&
    !isFolderDiscoveryLoading &&
    !isSearchActive &&
    !hasFolderContent;

  const hasFilteredAssets = (assets?.length || 0) > 0;
  const hasFilteredSubfolders = (filteredSubfolders?.length || 0) > 0;
  const hasSearchResults = hasFilteredAssets || hasFilteredSubfolders;
  const hasSearchError =
    isSearchActive &&
    (assetsError || folderError) &&
    !isAssetsLoading &&
    !isFoldersLoading;

  return (
    <FolderContext.Provider value={folder}>
      <TableProvider>
        <FolderHeader
          folder={folder as FolderTreeItem}
          folderId={folderId}
          isFoldersLoading={isFoldersLoading}
          assets={assets}
        />

        {isTrashFolderRoute && isZapAndCanvasUnifiedTrashEnabled && (
          <div className={styles.trashRetentionAlert}>
            <Alert
              variant="banner"
              status="info"
              title={t("alerts.trash-retention")}
            />
          </div>
        )}

        {shouldShowDocumentationTab && (
          <div
            className={styles.tabsWrapper}
            onClickCapture={handleTabsClickCapture}
          >
            <Tabs
              items={[
                {
                  href: "#assets",
                  text: (
                    <span className={styles.tabLabel}>
                      <Icon aria-hidden name="miscList" size={16} />
                      {t("tabs.assets")}
                    </span>
                  ),
                },
                {
                  href: "#documentation",
                  text: (
                    <span className={styles.tabLabel}>
                      <Icon aria-hidden name="fileGuide" size={16} />
                      {t("tabs.documentation")}
                    </span>
                  ),
                },
              ]}
              selectedItemHref={
                activeTab === "documentation" ? "#documentation" : "#assets"
              }
              onChooseItem={(item) => {
                const nextTab =
                  item.href === "#documentation" ? "documentation" : "assets";
                emitFolderDocumentationEvent({
                  optionSelected:
                    nextTab === "documentation"
                      ? "click_documentation_tab"
                      : "click_assets_tab",
                  folderId,
                });
                setActiveTab(nextTab);

                if (globalThis.history !== undefined) {
                  globalThis.history.pushState(undefined, "", item.href);
                }
              }}
              isItemSelected={(item) =>
                item.href ===
                (activeTab === "documentation" ? "#documentation" : "#assets")
              }
              size="medium"
            />
          </div>
        )}

        {shouldShowDocumentationTab && activeTab === "documentation" ? (
          <FolderDocumentation
            folderId={folderId}
            assets={assets || []}
            hasEligibleFolderAssetIds={hasEligibleFolderAssetIds}
            pinnedCanvasId={pinnedCanvasId}
            isPinnedCanvasIdLoading={isPinnedCanvasIdLoading}
            onPinCanvas={handlePinCanvas}
            onUnpinCanvas={handleUnpinCanvas}
            onCreateCanvas={handleCreateCanvas}
            onGenerateFromFolderAssets={async () => {
              const canvasId = await generateNewCanvasFromFolderAssets();
              if (canvasId) setAutoLayoutCanvasId(canvasId);
            }}
            onRegenerateFromFolderAssets={async () => {
              const canvasId = await regeneratePinnedCanvasFromFolderAssets();
              if (canvasId) setAutoLayoutCanvasId(canvasId);
            }}
            isCreating={isCreating}
            isPinning={isPinning}
            isGeneratingFromFolderAssets={isGeneratingFromFolderAssets}
            autoLayoutPinnedCanvasOnLoad={
              Boolean(pinnedCanvasId) && pinnedCanvasId === autoLayoutCanvasId
            }
            onAutoLayoutPinnedCanvasConsumed={() =>
              setAutoLayoutCanvasId(undefined)
            }
          />
        ) : (
          <>
            {isReadOnly && (
              <div className={styles.readOnlyFolderAlert}>
                <Alert
                  variant="default"
                  title={t("alerts.read-only-title")}
                  status="warning"
                >
                  {t("alerts.read-only-description")}
                </Alert>
              </div>
            )}

            {shouldShowEmptyFolderState ? (
              <>
                {isTrashFolderRoute ? (
                  <EmptyTableDisplay
                    headerText={t("empty-state.trash-empty")}
                    icon="actionTrash"
                    subHeaderText={t("empty-state.trash-description")}
                  />
                ) : (
                  <EmptyTableDisplay
                    headerText={t("empty-state.no-assets-message")}
                    icon="navFolder"
                    {...(isPersonalFolderRoute
                      ? {
                          actionButtonText: t("buttons.create-zap"),
                          actionButtonIcon: "formAdd" as const,
                          actionButtonHref: getCreateZapUrl(folderId),
                        }
                      : {
                          actionButtonText: t("buttons.create-subfolder"),
                          actionButtonColor: "tertiary",
                          onActionButton: () => {
                            // Cannot create a subfolder more than 5 levels deep
                            if (canCreateSubfolderLimit) {
                              emit({
                                optionSelected:
                                  FolderActionInteractionOption.CreateSubfolder,
                                interactionRawText: folder?.isShared
                                  ? FolderTypeQueryState.Shared
                                  : FolderTypeQueryState.Personal,
                              });
                              setCurrentFolder(folder);
                              setCurrentFolderModal(FolderModal.Create);
                            } else {
                              createNotification({
                                message: subfolderLimitMessage,
                                type: "warning",
                              });
                            }
                          },
                          secondActionButtonText: t("buttons.create-zap"),
                          secondActionButtonIcon: "formAdd" as const,
                          secondActionButtonHref: getCreateZapUrl(folderId),
                        })}
                    subHeaderText={t("empty-state.assets-description")}
                  />
                )}
              </>
            ) : (
              <>
                {isSearchEnabled && (
                  <div className={styles.searchWrapper}>
                    <Field
                      label={t("search.placeholder" as const)}
                      inputId="folder-search-input"
                      renderLabel={() => (
                        <span className={styles.searchLabel}>
                          {t("search.placeholder" as const)}
                        </span>
                      )}
                      renderInput={(inputProps) => (
                        <TextInput
                          {...inputProps}
                          id="folder-search-input"
                          placeholder={t("search.placeholder" as const)}
                          type="search"
                          value={searchQuery}
                          onChange={handleSearchChange}
                          renderIconBefore={({ iconSize }) => (
                            <Icon name="navSearch" size={iconSize} />
                          )}
                          renderIconAfter={({
                            iconSize,
                            isFocused,
                            isHovered,
                          }) => {
                            if (!searchQuery) return;
                            const iconColor =
                              isFocused || isHovered ? undefined : "GrayWarm6";
                            return (
                              <button
                                className={styles.searchClearButton}
                                onClick={handleClearSearch}
                                aria-label={t("search.clear-label" as const)}
                              >
                                <Icon
                                  canAcceptPointerEvents={false}
                                  color={iconColor}
                                  name="formX"
                                  size={iconSize}
                                />
                              </button>
                            );
                          }}
                        />
                      )}
                    />
                  </div>
                )}

                {hasSearchError && (
                  <div className={styles.searchNoResults}>
                    <Alert variant="default" status="error">
                      {t("search.error" as const)}
                    </Alert>
                  </div>
                )}

                {isSearchActive &&
                !hasSearchResults &&
                !isAssetsLoading &&
                !hasSearchError ? (
                  <div className={styles.searchNoResults}>
                    <Text color="GrayWarm6">
                      {t("search.no-results" as const)}
                    </Text>
                  </div>
                ) : (
                  <AssetTable
                    ariaLabel={t("accessibility.table-label", {
                      folderName: folder?.name || "",
                    })}
                    folders={filteredSubfolders || []}
                    folderId={folderId}
                    isCopyToAnotherAccountEnabled={isDuplicateAssetsEnabled}
                    assets={assets || []}
                    currentFolderDepth={ancestors?.length || 0}
                    isLoading={
                      isAssetsLoading ||
                      isFoldersLoading ||
                      isFolderDiscoveryLoading
                    }
                    showCheckboxes={
                      !isVirtualFolderNotDefault(folderId) ||
                      (isTrashFolderRoute && isZapAndCanvasUnifiedTrashEnabled)
                    }
                    showLocationColumn={isTrashIndexPath}
                    showLastRun={isLastRunEnabled && !isTrashFolderRoute}
                    bulkSelectItems={bulkSelectItems}
                    selectableEntities={selectableEntities}
                    onSortChange={handleSortChange}
                    isSortEnabled={isSortEnabled}
                    // The trash index cannot sort by status, so don't offer it.
                    isStatusSortEnabled={!isTrashIndexPath}
                    sortDescriptor={sortDescriptor}
                    sortDisabledTooltip={sortDisabledTooltip}
                    bulkActionsEmptyStateMessage={
                      isTrashFolderRoute
                        ? tBulkBar("empty-state.mixed-selection")
                        : undefined
                    }
                  />
                )}

                <BulkActionsBarWrapper />

                {hasServerPagination && (
                  <Pagination
                    page={page}
                    // Take the max of either the total asset pages or total subfolder/children pages
                    totalPages={Math.max(totalAssetsPages, totalSubfolderPages)}
                    totalCount={totalCount || 0}
                    isLoading={
                      isAssetsLoading ||
                      isFoldersLoading ||
                      isFolderDiscoveryLoading
                    }
                    allowPageSizeChange={isTrashIndexPath}
                  />
                )}
                {!hasServerPagination &&
                  !isAssetsLoading &&
                  !isFoldersLoading &&
                  (assets.length > 0 || filteredSubfolders.length > 0) && (
                    <Text tag="p" type="Body1">
                      {t("total-assets-count", {
                        totalCount: assets.length + filteredSubfolders.length,
                      })}
                    </Text>
                  )}

                {/* Zap Modals for bulk actions */}
                <ZapModals
                  currentModal={currentZapModal}
                  setCurrentModal={setCurrentZapModal}
                  currentZaps={currentZaps as HydratedZapAsset[]}
                  clearZapSelection={clearSelection}
                  folderId={folderId}
                  closeModalAndClearParameters={closeModalAndClearParameters}
                  setCurrentZap={setCurrentZap}
                />

                {isBulkMoveAssetsEnabled && (
                  <BulkMoveModal
                    selectedAssetIds={selectedTypedAssetIds}
                    isOpen={currentBulkModal === BulkModal.Move}
                    setIsOpen={(isOpen) => {
                      if (!isOpen) setCurrentBulkModal(undefined);
                    }}
                    onSettled={() => {
                      setCurrentBulkModal(undefined);
                      clearSelection();
                    }}
                    folderId={folderId}
                    permissionResult={permissionResult}
                  />
                )}
                {isZapAndCanvasUnifiedTrashEnabled && (
                  <AssetDeleteModal
                    selectedAssetIds={selectedTypedAssetIds}
                    isOpen={currentBulkModal === BulkModal.Delete}
                    setIsOpen={(isOpen) => {
                      if (!isOpen) setCurrentBulkModal(undefined);
                    }}
                    onSettled={() => {
                      setCurrentBulkModal(undefined);
                      clearSelection();
                    }}
                    folderId={folderId}
                    permissionResult={deletePermissionResult}
                    assetNamesById={assetNamesById}
                    singleAssetName={singleDeleteAssetName}
                  />
                )}
                <CopyToAnotherAccountModal
                  isOpen={currentBulkModal === BulkModal.CopyToAnotherAccount}
                  setIsOpen={(isOpen) => {
                    if (!isOpen) setCurrentBulkModal(undefined);
                  }}
                  assets={bulkCopyState?.assets ?? []}
                  copyEntryPoint="bulk_selection"
                  folderIds={bulkCopyState?.folderIds}
                  selectedItemsAreAllFolders={
                    bulkCopyState?.selectedItemsAreAllFolders ?? false
                  }
                />
              </>
            )}
          </>
        )}
      </TableProvider>
      <FolderModals
        currentModal={currentFolderModal}
        setCurrentModal={setCurrentFolderModal}
        currentFolder={currentFolder}
        setCurrentFolder={setCurrentFolder}
        closeModalAndClearParameters={closeModalAndClearParameters}
      />
    </FolderContext.Provider>
  );
}

export function Folder() {
  return (
    <div>
      <PageSizeProvider>
        <FolderWithPageSize />
      </PageSizeProvider>
    </div>
  );
}
